import { useState, useEffect } from "react";
import AgentChat from "./AgentChat";
import Header from "./Header";
import Footer from "./Footer";
import AsideMenu from "./SideMenu/AsideMenu";
import { Redirect } from "react-router";
import { connect } from "react-redux";
import {
  fetchApplicationAndDependencies,
  routeChanged,
  setAsideMenuCondensed,
  setAsideMenuCondensedPref,
} from "src/redux/actions";
import Routes from "../Routes";
import ErrorBoundary from "src/compositions/ErrorBoundary/index.js";
import { ContactI } from "src/redux/reducers/contactInfo";
import { ApplicationStateI } from "src/redux/reducers/application";
import { UserState } from "src/redux/reducers/user";
import ImpersonationModal from "./ImpersonationModal";
import { ImpersonationStateI } from "src/redux/reducers/impersonation";
import { useImpersonation } from "src/common/hooks/impersonation";
import { useSwitchAccount } from "src/common/hooks/externalAccount";
import { useSessionTimeout } from "src/common/hooks/sessionTimeout";
import { setGoogleAnalyticsEvent } from "src/common";
import { AriaLivePageTitle } from "./AriaLivePageTitle";
import { SSOLinkI } from "src/types/SSOLink";
import { UserI } from "src/types/User";
import { History } from "history";
import { ExternalAccountStateI } from "src/redux/reducers/externalAccount";
import SwitchAccountModal from "./SwitchAccountModal";
import { Box, IconButton } from "@chakra-ui/react";
import { ChevronLeftIcon } from "ctm-compass-react";
import { useLightMode } from "src/common/hooks/lightMode";
import { useTranslate } from "@tolgee/react";

interface SmartAppI {
  externalAccount?: {
    isTimedOut: boolean;
    isTimingOut: boolean;
    account: { companyName: string; companyID: string };
  };
  history: History;
  location: any;
  match: any;
  agentChat?: SSOLinkI;
  contactInfo?: { name: string; number: string }[];
  headerState: {
    isCondensed: boolean;
    isSticky: boolean;
  };
  load: () => void;
  user: UserI;
  portalState: {
    pageClassName: string;
    isWidgetVisible: boolean;
  };
  loading: boolean;
  asideMenuCondensed: boolean;
  asideMenuCondensedPref: boolean;
  impersonation: {
    isImpersonating: string;
    isTimingOut: boolean;
    isTimedOut: boolean;
  };
  redirectToLogout: boolean;
  setAsideMenuCondensed: (val: boolean) => void;
  setAsideMenuCondensedPref: (val: boolean) => void;
  routeLocationChanged: (params: { location: any }) => void;
}

const SmartApp = ({
  agentChat,
  contactInfo,
  externalAccount,
  headerState,
  history,
  load,
  loading,
  location,
  impersonation,
  redirectToLogout,
  setAsideMenuCondensed,
  setAsideMenuCondensedPref,
  user,
  portalState,
  asideMenuCondensed,
  asideMenuCondensedPref,
  routeLocationChanged,
}: SmartAppI) => {
  const { t } = useTranslate();
  const [isChatOpen, setChatOpen] = useState(false);
  const {
    initializeImpersonation,
    clearImpersonation,
    continueImpersonationSession,
  } = useImpersonation();
  const { initializeSwitchAccount, clearSwitchAccount } = useSwitchAccount();
  const { initializeSessionTimeout } = useSessionTimeout(user);
  const { lightMode } = useLightMode();

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user.roles) {
      initializeSessionTimeout();
    }
  }, [user.roles]);

  useEffect(() => {
    const payload = { location: window.location };
    routeLocationChanged(payload);
  }, [window.location.pathname]);

  const handleChatOpen = () => {
    setGoogleAnalyticsEvent({
      action: "portal_dashboard_agent_chat_click",
      category: (!isChatOpen).toString(),
    });
    setChatOpen(!isChatOpen);
  };

  const handleClick = () => {
    const newCondensed = !(asideMenuCondensed && asideMenuCondensedPref);
    setAsideMenuCondensed(newCondensed);
    if (window.location.pathname !== "/")
      setAsideMenuCondensedPref(newCondensed);
  };

  if (redirectToLogout) return <Redirect to="/logout" />;

  const isCondensed = asideMenuCondensed && asideMenuCondensedPref;
  const headerCondensed = headerState?.isCondensed ?? false;
  const { pageClassName } = portalState;
  const agentChatHref = agentChat?.href;

  // Derive border for impersonation / external account modes
  const pageBorder = impersonation?.isImpersonating
    ? "2px solid red"
    : externalAccount?.account
      ? "2px solid orange"
      : undefined;

  return (
    <ErrorBoundary addContainer addLayoutClass>
      <Box
        className={`layout ${pageClassName}`}
        display="flex"
        flexDirection="column"
        minH="100vh"
        border={pageBorder}
        data-aut="Portal|Container"
      >
        <Box
          as="a"
          href="#main-content"
          position="absolute"
          left="-10000px"
          top="auto"
          w="1px"
          h="1px"
          overflow="hidden"
          textAlign="center"
          display="block"
          p="0.25em"
          border="4px solid var(--primaryColor)"
          color="initial"
          _focus={{ position: "static", w: "auto", h: "auto" }}
        >
          {t({
            key: "dashboard.skipToMain",
            defaultValue: "Skip To Main Content",
          })}
        </Box>

        <Header
          ctmLogo="/images/ctm-portal-logo-white.svg"
          handleChatOpen={handleChatOpen}
          agentChatLink={agentChat}
          initializeImpersonation={initializeImpersonation}
          clearImpersonation={clearImpersonation}
          initializeSwitchAccount={initializeSwitchAccount}
          clearSwitchAccount={clearSwitchAccount}
          isImpersonating={impersonation?.isImpersonating}
          onToggleNav={handleClick}
        />

        <Box display="flex" position="relative" flex={1} id="page-container">
          <Box
            position={{ base: "absolute", md: "relative" }}
            flexShrink={0}
            w={{
              base: isCondensed ? "0" : "var(--asideExpandedWidth)",
              md: isCondensed
                ? "var(--asideCondensedWidth)"
                : "var(--asideExpandedWidth)",
            }}
            h={{ base: "100%", md: "auto" }}
            bg={{
              base: lightMode ? "white" : "rgba(0,0,0,0.8)",
              md: lightMode ? "white" : "var(--headerFooterColor)",
            }}
            transition="width 0.3s ease"
            zIndex={40}
            left={0}
            overflow="visible"
          >
            <Box overflow="hidden" w="100%" h="100%">
              <Box
                display={{ base: "none", md: "block" }}
                position="absolute"
                right={0}
                top={10}
                transform={`translate(50%, -50%) rotate(${isCondensed ? "180deg" : "0deg"})`}
                transition="transform 0.3s ease"
                zIndex={50}
              >
                <IconButton
                  onClick={handleClick}
                  aria-label={
                    isCondensed
                      ? t({
                          key: "dashboard.sideMenu.expandNav",
                          defaultValue: "Expand Nav",
                        })
                      : t({
                          key: "dashboard.sideMenu.collapseNav",
                          defaultValue: "Collapse Nav",
                        })
                  }
                  icon={<ChevronLeftIcon />}
                  isRound
                  backgroundColor="var(--primaryColor)"
                  color="white"
                  size="sm"
                  fontSize="12px"
                  w="24px"
                  h="24px"
                  minH="24px"
                  minW="24px"
                />
              </Box>
              <AsideMenu
                path={location.pathname}
                isCondensed={isCondensed}
                history={history}
                search={location.search}
                userLogo={user.logo}
              />
            </Box>
          </Box>

          <ErrorBoundary addContainer pageView>
            <Box
              as="main"
              id="main-content"
              flex={1}
              py={4}
              className={[
                "content",
                headerCondensed && "condensed-header",
                isCondensed && "nav-condensed",
              ]
                .filter(Boolean)
                .join(" ")}
              minH="calc(100vh - 158px - 70px)"
              maxW="var(--contentMaxWidth)"
              mx="auto"
              sx={{ container: "dashboard / inline-size" }}
            >
              <Routes user={user} loading={loading} location={location} />
            </Box>
          </ErrorBoundary>

          {isChatOpen && (
            <AgentChat
              agentChatHref={agentChatHref}
              handleClose={handleChatOpen}
            />
          )}
        </Box>

        {Boolean(impersonation?.isTimingOut || impersonation.isTimedOut) && (
          <ImpersonationModal
            isTimedOut={impersonation?.isTimedOut}
            clearImpersonation={clearImpersonation}
            continueImpersonationSession={continueImpersonationSession}
          />
        )}
        {Boolean(
          externalAccount?.isTimingOut || externalAccount?.isTimedOut,
        ) && (
          <SwitchAccountModal
            account={externalAccount?.account}
            clearSwitchAccount={clearSwitchAccount}
            isTimedOut={externalAccount?.isTimedOut}
          />
        )}

        <Footer contactInfo={contactInfo} />
        <AriaLivePageTitle />
      </Box>
    </ErrorBoundary>
  );
};

export default connect(
  (state: {
    agentChat: SSOLinkI;
    contactInfo: ContactI[];
    externalAccount: ExternalAccountStateI;
    application: ApplicationStateI;
    user: UserState;
    impersonation: ImpersonationStateI;
  }) => ({
    agentChat: state.agentChat,
    contactInfo: state.contactInfo,
    externalAccount: state.externalAccount,
    headerState: state.application.header,
    user: state.user.data,
    portalState: state.application.portal,
    loading: state.user.loading,
    redirectToLogout: state.application.redirectToLogout,
    asideMenuCondensed: state.application.asideMenuCondensed,
    asideMenuCondensedPref: state.application.asideMenuCondensedPref,
    impersonation: state.impersonation,
  }),
  (dispatch) => ({
    load: () => dispatch(fetchApplicationAndDependencies()),
    setAsideMenuCondensedPref: (payload) =>
      dispatch(setAsideMenuCondensedPref(payload)),
    setAsideMenuCondensed: (payload) =>
      dispatch(setAsideMenuCondensed(payload)),
    routeLocationChanged: (payload) => dispatch(routeChanged(payload)),
  }),
)(SmartApp);
